
import { Component, Ref, Vue } from 'vue-property-decorator'
import ResultProblem from '@/components/templates/study/ResultProblem.vue'
import ModalEscapeStudy from '@/views/modal/ModalEscapeStudy.vue'
import { StudyModel } from '@/models/StudyModel'
import { CurriculumUnit } from '@/models/CurriculumUnit'
import { DrawingModel } from '@/models/DrawingModel'
import { GeneralLearningsApi } from '@/api/methods/GeneralLearningsApi'
import { CurriculumUnitsApi } from '@/api/methods/CurriculumUnitsApi'
import { ProblemsApi } from '@/api/methods/ProblemsApi'
import { FixedExercisesApi } from '@/api/methods/FixedExercisesApi'
import { UserReviewProblemsApi } from '@/api/methods/UserReviewProblemsApi'
import { StudyGeneralLearningMemosApi } from '@/api/methods/study/StudyGeneralLearningMemosApi'
import StrokeUtils from '@/utils/StrokeUtils'

/**
 * 演習結果(通常学習)
 */
@Component({
  components: {
    ResultProblem,
    ModalEscapeStudy,
  },
})
export default class GeneralResult extends Vue {
  /** 学習終了モーダル */
  @Ref()
  private modalEscapeStudy?: ModalEscapeStudy

  /** 読み込み完了フラグ */
  private isLoaded = false

  /** 単元情報 */
  private curriculumUnit!: CurriculumUnit

  /** 固定問題ID */
  private fixedExerciseId: number = 0

  /** 小問情報 */
  private problem?: StudyModel.Problem

  /** 復習BOX登録済みフラグ */
  private isProblemSaved: boolean = false

  /** メモのストローク情報 */
  private memoStrokes: DrawingModel.Stroke[] = []

  /**
   * 演習結果ID
   *
   * @returns 演習結果ID
   */
  private get learningId(): number {
    return Number(this.$route.params.learningId)
  }

  /**
   * 戻り先情報が存在するか否かのフラグ
   *
   * @returns 戻り先情報が存在したらtrue
   */
  private get hasPrevPage(): boolean {
    return this.$prevPage.restorePage() !== null
  }

  /**
   * クエリ保持の教材ID
   *
   * @returns 教材ID
   */
  private get studyCategoryId(): number {
    return Number(this.$route.query.studyCategoryId)
  }

  /**
   * クエリ保持の系統図ID
   *
   * @returns 系統図ID
   */
  private get systemChartId(): number {
    return Number(this.$route.query.systemChartId)
  }

  /**
   * 演習終了条件を満たしたか否かのクエリ情報
   *
   * @returns 終了条件を満たしたか否か
   */
  private get isFinished(): boolean {
    return !!this.$route.query.finished
  }

  /**
   * 初回処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 演習結果を取得
    const generalLearning = await GeneralLearningsApi.getGeneralLearning(this.learningId)

    // 演習メモ情報を取得
    this.memoStrokes = await this.getLearningMemo(this.learningId)

    // 単元情報を取得
    const curriculumUnit = await CurriculumUnitsApi.getGeneral(generalLearning.curriculumUnitId)
    this.curriculumUnit = {
      id: curriculumUnit.id,
      name: curriculumUnit.name,
    }

    // 小問情報取得
    const problem = await ProblemsApi.getProblemCorrect(generalLearning.resultProblem.problemId)

    // 小問情報を生成
    this.problem = new StudyModel.Problem(problem.code, problem.imageDescriptions, problem.imageGuidances || [])

    // 設問情報を生成
    for (let i = 0; i < problem.questions.length; i++) {
      const question = problem.questions[i]
      const resultQuestion = generalLearning.resultProblem.resultQuestions[i]

      // 設問情報を追加
      this.problem.addQuestionByResponse(question, resultQuestion.isCorrect)

      // 回答情報を設定
      this.problem.questions[i].inputValues = resultQuestion.inputValues
    }

    // 固定問題情報を取得
    const fixedExercise = await FixedExercisesApi.getActive(generalLearning.curriculumUnitId, generalLearning.level)
    this.fixedExerciseId = fixedExercise.id

    // 復習BOXへの登録状況を更新
    await this.updateProblemSavedStatus()

    this.isLoaded = true

    // 終了条件を満たしている場合はモーダル表示
    if (this.isFinished) {
      // モーダル要素の読み込み待ち
      while (!this.modalEscapeStudy) {
        await this.sleep(10)
      }
      this.modalEscapeStudy.show()
    }

    this.$loading.complete()
  }

  /**
   * 演習メモの取得
   *
   * @param learningId 演習ID
   * @returns 演習メモのストローク情報
   */
  private async getLearningMemo(learningId: number): Promise<DrawingModel.Stroke[]> {
    // API実行・再変換
    try {
      const strokes = await StudyGeneralLearningMemosApi.getGeneralLearningMemo(learningId)

      return StrokeUtils.compressedJsonToStrokes(strokes.strokes)
    } catch {
      // 例外発生時は無視して空配列を返す
      return []
    }
  }

  /**
   * 復習BOXへの登録状況を更新
   */
  private async updateProblemSavedStatus(): Promise<void> {
    // nullブロック(実際には発生しない)
    if (!this.problem) {
      return
    }
    const reviewRegistered = await UserReviewProblemsApi.getRegistered(this.fixedExerciseId, this.problem.problemCode)
    this.isProblemSaved = reviewRegistered.isRegistered
  }

  /**
   * 復習BOXへの登録処理
   */
  private async eventSaveProblem(): Promise<void> {
    // nullブロック(実際には発生しない)
    if (!this.problem) {
      return
    }
    this.$loading.start()
    await UserReviewProblemsApi.postCreate(this.fixedExerciseId, this.problem.problemCode)
    await this.updateProblemSavedStatus()
    this.$loading.complete()
  }

  /**
   * 復習BOXへの登録解除処理
   */
  private async eventRemoveProblem(): Promise<void> {
    // nullブロック(実際には発生しない)
    if (!this.problem) {
      return
    }
    this.$loading.start()
    await UserReviewProblemsApi.postDelete(this.fixedExerciseId, this.problem.problemCode)
    await this.updateProblemSavedStatus()
    this.$loading.complete()
  }

  /**
   * 戻るボタン押下時処理
   */
  private async eventReturn(): Promise<void> {
    // 戻り先情報が存在する場合
    const returnPage = this.$prevPage.restorePage()
    if (returnPage) {
      await this.$router.push({
        path: returnPage.path,
        query: returnPage.query,
      })
      return
    }

    // クエリのセット
    let query = {}
    if (this.studyCategoryId) {
      // 教材ID
      query = { studyCategoryId: `${this.studyCategoryId}` }
    } else if (this.systemChartId) {
      // 系統図ID
      query = { systemChartId: `${this.systemChartId}` }
    }

    await this.$router.push({
      path: `/student/general/${this.curriculumUnit.id}`,
      query: query,
    })
  }

  /**
   * トップページボタン押下時処理
   */
  private async eventTopPage(): Promise<void> {
    await this.$router.push('/student/top')
  }

  /**
   * ウェイト処理
   *
   * @param ms ミリ秒
   */
  private async sleep(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms))
  }
}
